import * as React from 'react'

import { TMSubmitOrderVariables, useMSubmitOrder } from '~graphql/generated/clientGraphql'
import { SubmitHandler } from '~shared/segments/Form'
import {
  getSubmitVariables as getSubmitVariablesShared,
  TOrderForm,
  useFormOrderCtx,
} from '~shared/segments/FormScreen'
import { isPostPaidMethod } from '~shared/segments/PaymentMethods'

import { getPaymentCheckScreenRoute } from '../../../helpers/getPaymentCheckScreenRoute'
import { useOrderSubmitHandlers } from './useOrderSubmitHandlers'

/**
 * @Returns `submitOrder` callback to:
 * - submit the form values to the BE,
 * - handle payment.
 */
export const useOrderSubmit = () => {
  const [mSubmitOrder] = useMSubmitOrder()
  const submitHandlers = useOrderSubmitHandlers()

  const order = useFormOrderCtx()

  // transform form into variables, submit them to BE via M,
  // retrieve payment gate URL (for pre-pay methods) & navigate to the next step
  const submitOrder: SubmitHandler<TOrderForm> = React.useCallback(
    async (formValues) => {
      const { navigateToGateway, navigateToSubmittedScreen, getUrlsFromResult, onSubmitFailed } =
        submitHandlers

      // without this we shouldn’t ever get here
      if (formValues.paymentMethod === null || !order) {
        onSubmitFailed()

        return
      }

      // send order info (from the OrderForm) to the BE to be validated and get necessary info to proceed with the payment
      const variables = getSubmitVariables({
        orderId: order.id,
        formValues: {
          ...formValues,
          paymentMethod: formValues.paymentMethod,
        },
      })

      // send order info (from the OrderForm) to the BE to be validated and handle errors
      const { data, errors } = await mSubmitOrder({ variables, fetchPolicy: `network-only` })

      if (errors) return

      // get gatewayUrls to proceed with the payment
      const gatewayUrls = getUrlsFromResult(data?.submitResult ?? null)

      if (!gatewayUrls) {
        return
      }

      // * post-paid methods
      if (isPostPaidMethod(formValues.paymentMethod.paymentGate.enum)) {
        navigateToSubmittedScreen(order.id)
        return
      }

      // * pre-paid methods
      navigateToGateway({ gatewayUrls, orderId: order.id })
    },
    [mSubmitOrder, order, submitHandlers],
  )

  return { submitOrder }
}

type TSubmitVariables = Pick<FirstParam<typeof getSubmitVariablesShared>, 'formValues' | 'orderId'>

// transform order form inputs for SubmitOrder M
const getSubmitVariables = ({ orderId, formValues }: TSubmitVariables): TMSubmitOrderVariables => {
  const paymentCheckScreenRoute = getPaymentCheckScreenRoute()

  return getSubmitVariablesShared({
    orderId,
    formValues,
    paymentCheckScreenRoute,
  })
}
